import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Share2, Users, DollarSign, ArrowRight, LogIn } from 'lucide-react';
import { useAuthStore } from '../../context/useAuthStore';

const steps = [
  { icon: Share2, title: 'Share Your Link', desc: "Sign in, open any eligible book, and generate a unique affiliate link to share with your audience — the option only shows once you're logged in." },
  { icon: Users, title: 'Readers Purchase', desc: 'When someone buys through your link, the sale is tracked straight to your account.' },
  { icon: DollarSign, title: 'Earn Commission', desc: "Earn the book's commission rate on every qualifying sale — request a payout anytime." },
];

export default function AffiliateProgramSection() {
  const user = useAuthStore((s) => s.user);

  return (
    <section className="container-app py-14">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-xl3 bg-gradient-primary px-6 py-10 sm:px-12 sm:py-14 text-white"
      >
        <div className="absolute inset-0 bg-gradient-radial-glow" />
        <div className="relative">
          <div className="max-w-2xl">
            <p className="eyebrow mb-2 !text-white/70">Affiliate Program</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">How the affiliate link works</h2>
            <p className="mt-2 text-sm sm:text-base text-white/80">
              Recommend books you love and earn a commission on every sale made through your link — it's that simple.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass rounded-xl2 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                  <s.icon size={18} />
                </span>
                <p className="mt-3 font-display font-semibold">{s.title}</p>
                <p className="mt-1 text-sm text-white/80 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          {!user && (
            <p className="mt-5 flex items-start gap-1.5 text-xs text-white/70">
              <LogIn size={13} className="mt-0.5 shrink-0" />
              You need to sign in first — the "Earn Commission" option on a book's page only appears once you're logged in.
            </p>
          )}

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
            <Link
              to={user ? '/account/affiliate' : '/signup'}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary-700 font-semibold px-6 py-3 shadow-glow hover:brightness-95 transition text-center"
            >
              {user ? 'Go to Your Dashboard' : 'Start Earning'} <ArrowRight size={16} className="shrink-0" />
            </Link>
            <Link to="/affiliate" className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white transition-colors">
              Learn more about the program <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
